import React from 'react';
import { Helmet } from 'react-helmet-async';

// Organization Schema
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Safarnamah',
    'url': 'https://safarnamah.in',
    'logo': 'https://safarnamah.in/logo.png',
    'description': 'Personalized travel guides and trip planning for Southeast Asian destinations by Saket Bundele and Pallavi Korade',
    'keywords': 'Safarnamah, Saket, saketbundele, safarnama, saket bundele safarnama, saket bundele, Saket bundele safarnamah, safrnam, pallavi korade',
    'foundingDate': '2024',
    'founders': [
      {
        '@type': 'Person',
        'name': 'Saket Bundele'
      },
      {
        '@type': 'Person',
        'name': 'Pallavi Korade'
      }
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'url': 'https://safarnamah.in/contact',
      'contactType': 'Customer Service',
      'email': 'contact@safarnamah.in'
    },
    'sameAs': [
      'https://www.facebook.com/safarnamah',
      'https://www.instagram.com/safarnamah',
      'https://www.twitter.com/safarnamah'
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Website Schema
export const WebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Safarnamah',
    'url': 'https://safarnamah.in',
    'description': 'Personalized travel guides and trip planning for Southeast Asian destinations by Saket Bundele and Pallavi Korade',
    'keywords': 'Safarnamah, Saket, saketbundele, safarnama, saket bundele safarnama, saket bundele, Saket bundele safarnamah, safrnam, pallavi korade',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': 'https://safarnamah.in/search?q={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items = [] }) => {
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbList)}
      </script>
    </Helmet>
  );
};

// Destination Schema
export const DestinationSchema = ({ destination, description, image, url }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    'name': destination,
    'description': description,
    'url': url,
    'image': image,
    'areaServed': 'Worldwide',
    'award': 'Recommended by Safarnamah Travel Guides'
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Trip Schema
// Also emitted by the prerenderer, but that copy is gone once React mounts, so a
// crawler that renders JavaScript would otherwise see no itinerary at all.
export const TripSchema = ({ name, description, url, stops = [] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    'name': name,
    'description': description,
    'url': url,
    'itinerary': {
      '@type': 'ItemList',
      'numberOfItems': stops.length,
      'itemListElement': stops.map((stop, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'item': {
          '@type': 'TouristDestination',
          'name': stop.name,
          'description': stop.note,
        },
      })),
    },
    'provider': {
      '@type': 'Organization',
      'name': 'Safarnamah',
      'url': 'https://safarnamah.in',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Item List Schema
// Also emitted by the prerenderer, but that copy is gone once React mounts.
export const ItemListSchema = ({ name, items = [] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': name,
    'numberOfItems': items.length,
    'itemListElement': items.map((item, i) => ({
      '@type': 'ListItem',
      'position': i + 1,
      'name': item,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Hotel Schema
export const HotelSchema = ({ name, description, image, url, rating, reviewCount, priceRange, address, telephone }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    'name': name,
    'description': description,
    'url': url,
    'image': image,
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': address?.city || '',
      'addressCountry': address?.country || ''
    },
    'telephone': telephone || '',
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': rating || '4.5',
      'reviewCount': reviewCount || '100'
    },
    'priceRange': priceRange || '$$$'
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Blog Article Schema
export const ArticleSchema = ({ title, description, image, url, datePublished, dateModified, author }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': title,
    'description': description,
    'image': image,
    'url': url,
    'datePublished': datePublished || new Date().toISOString(),
    'dateModified': dateModified || new Date().toISOString(),
    'author': {
      '@type': 'Person',
      'name': author || 'Safarnamah Team',
      'url': 'https://safarnamah.in/about'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Safarnamah',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://safarnamah.in/logo.png'
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': url
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQSchema
export const FAQSchema = ({ faqs = [] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

const SchemaMarkupExports = {
  OrganizationSchema,
  WebsiteSchema,
  BreadcrumbSchema,
  DestinationSchema,
  HotelSchema,
  ArticleSchema,
  FAQSchema
};

export default SchemaMarkupExports;
